import React, { useState, useEffect, useContext } from 'react';
import { Container, Nav, Tab } from 'react-bootstrap';
import axios from 'axios';
import AppContext from '../../../context/AppContext';
import AdmHotelBookingStats   from '../../../subscreens/admin/AdminPaymentAndBookingManagement/AdmHotelBookingStats';
import AdmHotelBookingFilters from '../../../subscreens/admin/AdminPaymentAndBookingManagement/AdmHotelBookingFilters';
import AdmHotelBookingTable   from '../../../subscreens/admin/AdminPaymentAndBookingManagement/AdmHotelBookingTable';
import AdmHotelPaymentStats   from '../../../subscreens/admin/AdminPaymentAndBookingManagement/AdmHotelPaymentStats';
import AdmHotelPaymentFilters from '../../../subscreens/admin/AdminPaymentAndBookingManagement/AdmHotelPaymentFilters';
import { AdmHotelPaymentTable, AdmVendorSettlementTable } from '../../../subscreens/admin/AdminPaymentAndBookingManagement/AdmHotelPaymentTable';

import '../../../assets/css/admin/adminPaymentAndBookingManagement/adminbooking.css';

const computePaymentsFromBookings = (bookings) => {
  const payments = [];
  bookings.forEach(booking => {
    const payment = booking.jjpayment;
    if (payment && payment.id) {
      payments.push({
        id: payment.id,
        bookingId: booking.id,
        type: 'user_payment',
        amount: payment.total_amount || booking.total_amount,
        commission: payment.admin_commission || 0,
        vendorPayout: payment.vendor_amount || 0,
        status: payment.status === 'paid' ? 'paid' : (payment.status || 'pending'),
        paymentMethod: payment.payment_method,
        transactionId: payment.stripe_payment_intent_id,
        date: payment.paid_at || booking.created_at,
        vendor: booking.jjvendor,
        customerName: booking.jjcustomer?.cus_username,
        payoutStatus: payment.status === 'paid' ? 'completed' : 'pending',
      });

      if (payment.status === 'paid' && payment.vendor_amount > 0) {
        payments.push({
          id: `${payment.id}_vendor`,
          bookingId: booking.id,
          type: 'vendor_payout',
          amount: payment.vendor_amount,
          commission: payment.admin_commission,
          vendorPayout: payment.vendor_amount,
          status: payment.vendor_payout_status || 'pending',
          paymentMethod: null,
          transactionId: null,
          date: null,
          vendor: booking.jjvendor,
          payoutDueDate: null, 
        });
      }
    }
  });
  return payments;
};
const calcPaymentStats = (payments, bookings) => ({
  totalRevenue:     bookings.filter(b => b.payment_status === 'paid').reduce((s, b) => s + (b.jjpayment?.total_amount || b.total_amount), 0),
  totalCommission:  bookings.filter(b => b.payment_status === 'paid').reduce((s, b) => s + (b.jjpayment?.admin_commission || 0), 0),
  pendingPayouts:   payments.filter(p => p.type === 'vendor_payout' && p.status === 'pending').reduce((s, p) => s + p.amount, 0),
  completedPayouts: payments.filter(p => p.type === 'vendor_payout' && p.status === 'paid').reduce((s, p) => s + p.amount, 0),
  refundedAmount:   payments.filter(p => p.type === 'refund').reduce((s, p) => s + p.amount, 0),
});

const AdminHotelVendorManagement = () => {
  const { authToken, baseUrl, setPayments,setSelectedBookingId, setSelectedPaymentId } = useContext(AppContext);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [activeSubTab, setActiveSubTab] = useState('user-payments');
  const [bookingFilters, setBookingFilters] = useState({});
  const [paymentFilters, setPaymentFilters] = useState({});

  useEffect(() => {
    setLoading(true);
    axios
      .post(`${baseUrl}/admin/view-booking`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then(res => {
        const data = res.data?.data || [];
        const hotelBookings = data.filter(b => b.booking_type === 'hotel');
        setAllBookings(hotelBookings);
      })
      .catch(err => {
        console.error('Failed to fetch bookings', err);
        setAllBookings([]);
      })
      .finally(() => setLoading(false));
  }, [baseUrl, authToken]);

  const vendors = React.useMemo(() => {
    const vendorMap = new Map();
    allBookings.forEach(b => {
      const v = b.jjvendor;
      if (v && v.id && !vendorMap.has(v.id)) {
        vendorMap.set(v.id, { id: v.id, name: v.name, verification_status: v.verification_status });
      }
    });
    return Array.from(vendorMap.values());
  }, [allBookings]);

  const [manualRefunds, setManualRefunds] = useState([]);
  const payments = React.useMemo(() => {
  const base = computePaymentsFromBookings(allBookings);
    return [...base, ...manualRefunds];
  }, [allBookings, manualRefunds]);

  const getFilteredBookings = () => {
    let data = [...allBookings];
    const f = bookingFilters;
    if (f.search) {
      const q = f.search.toLowerCase();
      data = data.filter(b =>
        String(b.id).toLowerCase().includes(q) ||
        (b.jjcustomer?.cus_username || '').toLowerCase().includes(q) ||
        (b.jjvendor?.jjhotelregistration?.hotel_name || '').toLowerCase().includes(q)
      );
    }
    if (f.status && f.status !== 'all') data = data.filter(b => b.booking_status === f.status);
    if (f.paymentStatus && f.paymentStatus !== 'all') data = data.filter(b => b.payment_status === f.paymentStatus);
    if (f.vendor && f.vendor !== 'all') data = data.filter(b => b.jjvendor?.id === parseInt(f.vendor));
    if (f.startDate && f.endDate) {
      data = data.filter(b => {
        const d = new Date(b.created_at);
        return d >= new Date(f.startDate) && d <= new Date(f.endDate);
      });
    }
    return data;
  };

  const filteredBookings = getFilteredBookings();

  const getFilteredPayments = () => {
    let data = [...payments];
    const f = paymentFilters;
    if (f.search) {
      const q = f.search.toLowerCase();
      data = data.filter(p =>
        String(p.id).toLowerCase().includes(q) ||
        String(p.bookingId).toLowerCase().includes(q) ||
        (p.customerName || '').toLowerCase().includes(q)
      );
    }
    if (f.status && f.status !== 'all') data = data.filter(p => p.status === f.status);
    if (f.type && f.type !== 'all') data = data.filter(p => p.type === f.type);
    if (f.vendor && f.vendor !== 'all') data = data.filter(p => p.vendor?.id === parseInt(f.vendor));
    return data;
  };

  const filteredPayments = getFilteredPayments();

  const handleViewPaymentFromBooking = (bookingId) => {
    const payment = payments.find(p => p.bookingId === bookingId && p.type === 'user_payment');
    if (payment) {
      setSelectedPaymentId(payment.id);
      setActiveTab('payments');
      setActiveSubTab('user-payments');
      setPaymentFilters(prev => ({ ...prev, search: bookingId }));
    }
  };

  const handleViewBookingFromPayment = (bookingId) => {
    if (bookingId) {
      setSelectedBookingId(bookingId);
      setActiveTab('bookings');
      setBookingFilters(prev => ({ ...prev, search: bookingId }));
    }
  };

  const handleRefundProcess = (bookingId, amount, reason) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const booking = allBookings.find(b => b.id === bookingId);
        const refundRecord = {
          id: `refund_${Date.now()}`,
          bookingId,
          type: 'refund',
          amount,
          commission: 0,
          vendorPayout: 0,
          status: 'refunded',
          date: new Date().toISOString(),
          vendor: booking?.jjvendor,
          customerName: booking?.jjcustomer?.cus_username,
          refundReason: reason,
        };
        setManualRefunds(prev => [...prev, refundRecord]);
        setAllBookings(prev => prev.map(b =>
          b.id === bookingId
            ? { ...b, payment_status: 'refunded', refundAmount: amount, refundDate: new Date().toISOString(), refundReason: reason }
            : b
        ));
        resolve();
      }, 700);
    });
  };

  const handleApproveSettlement = (payoutId, method, txnId, notes) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        setPayments(prev => prev.map(p =>
          p.id === payoutId
            ? { ...p, status: 'paid', paymentMethod: method, transactionId: txnId, date: new Date().toISOString() }
            : p
        ));
        resolve();
      }, 700);
    });
  };

  const paymentStats = calcPaymentStats(payments, allBookings);

  if (loading) {
    return (
      <div className="adm-bkd-screen d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
        <span className="ms-3">Loading hotel bookings...</span>
      </div>
    );
  }

  return (
    <div className="adm-bkd-screen">
      <Container fluid>

        <div className="adm-bkd-page-header">
          <div>
            <h2 className="adm-bkd-page-title">Hotel Vendor Management</h2>
            <p className="adm-bkd-page-subtitle">Manage bookings, payments, and vendor settlements</p>
          </div>
        </div>

        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>

          <Tab.Content>
            <Tab.Pane eventKey="bookings">
              <AdmHotelBookingStats bookings={allBookings} />
              <AdmHotelBookingFilters vendors={vendors} onFilterChange={setBookingFilters} />
              <AdmHotelBookingTable
                bookings={filteredBookings}
                onViewPayment={handleViewPaymentFromBooking}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="payments">
              <AdmHotelPaymentStats stats={paymentStats} />

              <Tab.Container activeKey={activeSubTab} onSelect={setActiveSubTab}>
                <Nav variant="tabs" className="adm-bkd-sub-tabs mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="user-payments" className="adm-bkd-sub-tab">
                      <i className="bi bi-person-check me-2" />User Payments
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="vendor-settlements" className="adm-bkd-sub-tab">
                      <i className="bi bi-building me-2" />Vendor Settlements
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content>
                  <Tab.Pane eventKey="user-payments">
                    <AdmHotelPaymentFilters vendors={vendors} onFilterChange={setPaymentFilters} />
                    <AdmHotelPaymentTable
                      payments={filteredPayments}
                      bookings={allBookings}
                      onViewBooking={handleViewBookingFromPayment}
                      onRefundProcess={handleRefundProcess}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="vendor-settlements">
                    <AdmVendorSettlementTable
                      payments={filteredPayments}
                      onApprove={handleApproveSettlement}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </div>
  );
};

export default AdminHotelVendorManagement;